import { createSelector, createSlice } from '@reduxjs/toolkit';
import { sortBy } from 'lodash';
import memoize from 'lodash/memoize';
import moment from 'moment';
import { SUBSCRIBER_ID_HEADER } from '../api/api.constants';
import { sendDataDeprecated as getPostServicePromiseDeprecated } from '../api/services.api';
import { createGenericThunk, createServicesThunk } from '../api/thunk.helper';
import { getCodeTableDictionary } from '../codeTables/codeTables.state';
import { CodeTableNumber, InvAction, LabelValuePair } from '../interfaces';
import { InteractionCategory } from './interactions.constants';
import {
  InteractionsStore,
  InteractionThumbnail,
  RetrieveInteractionRequest,
  RetrieveInteractionResponse,
  SearchCriteria,
  SearchInteractionsRequest,
  SearchInteractionsResponse
} from './interactions.interfaces';

// Actions
const searchInteractionsThunk = createGenericThunk<
  SearchInteractionsRequest,
  SearchInteractionsResponse
>('searchInteractions');

export const searchInteractions = (
  request: SearchInteractionsRequest,
  subscriberId?: number
) => {
  return searchInteractionsThunk({
    apiFunc: () =>
      getPostServicePromiseDeprecated<SearchInteractionsRequest>(
        {
          path: 'Configuration/SearchInteractions',
          headers: subscriberId ? { [SUBSCRIBER_ID_HEADER]: subscriberId } : {}
        },
        request
      ),
    useLoadingIndicator: true
  });
};

export const retrieveInteraction = createServicesThunk<
  RetrieveInteractionRequest,
  RetrieveInteractionResponse
>({
  path: 'Configuration/RetrieveInteraction',
  useLoadingIndicator: true
});

// Reducer
const initialState: InteractionsStore = {
  interaction: null,
  interactions: [],
  interactionsPageCount: 0,
  interactionsRecordCount: 0,
  searchCriteria: {
    startDate: moment().subtract(7, 'days').toISOString(),
    endDate: moment().toISOString(),
    interactionCategory: undefined,
    interactionType: undefined,
    pageNumber: 1,
    pageSize: 25
  }
};

const slice = createSlice({
  name: 'interactions',
  initialState,
  reducers: {
    clearInteraction: (state) => {
      state.interaction = initialState.interaction;
    },
    clearSearchCriteria: (state) => {
      state.searchCriteria = initialState.searchCriteria;
    },
    setSearchCriteria: (state, action: InvAction<SearchCriteria>) => {
      state.searchCriteria = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(searchInteractionsThunk.fulfilled, (state, action) => {
      state.interactions = action.payload.Interactions;
      state.interactionsPageCount = action.payload.PageCount;
      state.interactionsRecordCount = action.payload.RecordCount;
    });

    builder.addCase(retrieveInteraction.fulfilled, (state, action) => {
      state.interaction = action.payload.Interaction;
    });
  }
});

export const { clearSearchCriteria, setSearchCriteria } = slice.actions;

export default slice.reducer;

// Selectors
const InteractionsState = (state: {
  core: { interactions: InteractionsStore };
}) => {
  return state.core.interactions;
};

const InteractionListSelector = createSelector([InteractionsState], (state) => {
  return state.interactions;
});

export const InteractionsRecordCountSelector = createSelector(
  [InteractionsState],
  (state) => {
    return state.interactionsRecordCount;
  }
);

export const InteractionSearchCriteriaSelector = createSelector(
  [InteractionsState],
  (state) => {
    return state.searchCriteria;
  }
);

export const InteractionsPageCountSelector = createSelector(
  [InteractionsState],
  (state) => {
    return state.interactionsPageCount;
  }
);

const EMPTY_ARRAY: InteractionThumbnail[] = [];

export const InteractionsSelector = createSelector(
  [
    InteractionListSelector,
    getCodeTableDictionary(CodeTableNumber.InteractionType)
  ],
  (interactions, interactionTypes): InteractionThumbnail[] => {
    return (interactions || EMPTY_ARRAY).map((interaction) => {
      return {
        ...interaction,
        TypeName: interactionTypes[interaction.Type]?.Name || ''
      };
    });
  }
);

export const InteractionSelector = createSelector(
  [InteractionsState],
  (state) => {
    return state.interaction;
  }
);

export const InteractionCategoryOptionsSelector = createSelector(
  [getCodeTableDictionary(CodeTableNumber.InteractionCategory)],
  (codes): LabelValuePair[] => {
    return sortBy(
      Object.values(codes)
        .filter((code) => !code.AdditionalProperties.content_studio_display)
        .map((item) => {
          return {
            label: item.Name,
            value: item.Value
          };
        }),
      ['label']
    );
  }
);

export const InteractionTypeOptionsSelector = memoize(
  (interactionCategory?: InteractionCategory) => {
    return createSelector(
      [getCodeTableDictionary(CodeTableNumber.InteractionType)],
      (codes): LabelValuePair[] => {
        return sortBy(
          Object.values(codes)
            .filter((code) =>
              interactionCategory && !Number.isNaN(interactionCategory)
                ? +code.AdditionalProperties.interaction_category_code ===
                  +interactionCategory
                : true
            )
            .map((item) => {
              return {
                label: item.Name,
                value: item.Value
              };
            }),
          ['label']
        );
      }
    );
  }
);
